import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { ColumnsMenu } from 'components/ColumnsMenu';

const useTableColumnsList = ({ tableName, defaultColumns, filterColumns }) => {
    const qc = useQueryClient();
    const query = useQuery({
        queryKey: ['table-columns', tableName],
        queryFn: () => defaultColumns,
        enabled: false,
        initialData: defaultColumns,
    });
    const updateColumns = (newColumns) => {
        qc.setQueryData(['table-columns', tableName], newColumns);
    };
    const queryData = query.data;
    const data = useMemo(
        () =>
            typeof filterColumns === 'function'
                ? filterColumns(queryData)
                : queryData,
        [queryData, filterColumns],
    );
    return [data, updateColumns];
};

const defaultGetOptionFromMetric = (metric) => {
    const { key, label } = metric.getTableColumnConfig();
    return { key, label };
};

const useTableColumns = ({
    tableName,
    metrics,
    setSortOption,
    sortOption,
    filterColumns,
    getOptionFromMetric = defaultGetOptionFromMetric,
    buttonProps,
}) => {
    const defaultColumnOrder = metrics.map(([spateMetric]) => spateMetric.key);
    const defaultVisibleColumns = metrics
        .filter(([, visible]) => visible)
        .map(([spateMetric]) => spateMetric.key);
    const metricsKey = metrics
        .map(
            ([spateMetric, visible]) =>
                spateMetric.key + (visible ? '-visible' : ''),
        )
        .join(',');
    const [columnsOrder, setColumnsOrder] = useTableColumnsList({
        tableName: `${tableName}-${metricsKey}-col-order`,
        defaultColumns: defaultColumnOrder,
        filterColumns,
    });
    const [visibleColumns, setVisibleColumns] = useTableColumnsList({
        tableName: `${tableName}-${metricsKey}-visibility`,
        defaultColumns: defaultVisibleColumns,
        filterColumns,
    });
    useEffect(() => {
        if (sortOption?.sortBy && !visibleColumns.includes(sortOption.sortBy)) {
            setVisibleColumns([sortOption.sortBy, ...visibleColumns]);
        }
    }, []);

    const orderedMetrics = columnsOrder.map(
        (key) => metrics.find(([spateMetric]) => spateMetric.key === key)?.[0],
    );
    const visibleOrderedMetrics = orderedMetrics.filter(({ key }) =>
        visibleColumns.includes(key),
    );
    const columnsMenu = (
        <ColumnsMenu
            options={orderedMetrics.map((metric) => {
                return {
                    ...getOptionFromMetric(metric),
                    checked: visibleColumns.includes(metric.key),
                };
            })}
            onToggle={(option) => {
                setVisibleColumns((prev) => {
                    if (option.checked) {
                        return prev.filter((col) => col !== option.key);
                    }
                    return [option.key, ...prev];
                });
            }}
            onMove={(dragIdx, hoverIdx) => {
                setColumnsOrder((prev) => {
                    const next = prev.slice();
                    const [removed] = next.splice(dragIdx, 1);
                    next.splice(hoverIdx, 0, removed);
                    return next;
                });
            }}
            {...(buttonProps && { buttonProps })}
        />
    );
    const extendedSetSortOption = (nextSortOption) => {
        if (!visibleColumns.includes(nextSortOption.sortBy)) {
            setVisibleColumns([nextSortOption.sortBy, ...visibleColumns]);
        }
        setSortOption(nextSortOption);
    };
    return {
        visibleOrderedMetrics,
        columnsMenu,
        setSortOption: extendedSetSortOption,
        setVisibleColumns,
        setColumnsOrder,
        orderedMetrics,
    };
};

export default useTableColumns;
